<template>
  <div>
    <v-container>
      <v-data-table
        :headers="headers"
        :items="schedules"
        :loading="loading"
        item-key="schedule_id"
        :item-class="rowClass"
        hide-default-footer
        class="schedules-table"
        no-data-text="No scheduled deliveries yet">
        <template v-slot:[`item.template_id`]="{ item }">
          {{ templateName(item.template_id) }}
        </template>
        <template v-slot:[`item.frequency`]="{ item }">
          {{ frequencyLabel(item) }}
        </template>
        <template v-slot:[`item.recipients`]="{ item }">
          <v-tooltip top :disabled="!(item.recipients && item.recipients.length)">
            <template v-slot:activator="{ on }">
              <div class="recipients-cell text-truncate" v-on="on">
                {{ (item.recipients || []).join(', ') }}
              </div>
            </template>
            <div v-for="(recipient, i) in (item.recipients || [])" :key="i">
              {{ recipient }}
            </div>
          </v-tooltip>
        </template>
        <template v-slot:[`item.active`]="{ item }">
          <v-switch
            :input-value="item.active"
            color="success"
            dense
            hide-details
            :ripple="false"
            class="ma-0 pa-0 d-inline-flex"
            @change="toggleActive(item)">
          </v-switch>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon small class="mr-3" v-on="on" @click="editSchedule(item)">mdi-pencil</v-icon>
            </template>
            <span>Edit</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon small color="error" v-on="on" @click="promptDelete(item)">mdi-delete</v-icon>
            </template>
            <span>Delete</span>
          </v-tooltip>
        </template>
      </v-data-table>

      <v-divider class="mt-4 mb-4"></v-divider>

      <v-alert
        v-if="!loading && templates.length === 0"
        dense text type="info" class="mb-3">
        You have no saved export templates yet. Create one in the Templates tab to schedule its delivery.
      </v-alert>

      <div class="schedule-form">
        <div class="subtitle-2 mb-2">{{ editingId ? 'Edit schedule' : 'New schedule' }}</div>
        <v-row dense>
          <v-col cols="6">
            <v-select
              v-model="form.template_id"
              :items="templates"
              item-text="name"
              item-value="template_id"
              label="Template"
              dense outlined hide-details>
            </v-select>
          </v-col>
          <v-col cols="3">
            <v-select
              v-model="form.frequency"
              :items="frequencyOptions"
              label="Frequency"
              dense outlined hide-details>
            </v-select>
          </v-col>
          <v-col cols="3">
            <v-select
              v-if="form.frequency === 'weekly'"
              v-model="form.day_of_week"
              :items="weekdayOptions"
              label="Day of week"
              dense outlined hide-details>
            </v-select>
            <v-text-field
              v-else-if="form.frequency === 'monthly'"
              v-model.number="form.day_of_month"
              type="number"
              min="1"
              max="31"
              label="Day of month"
              dense outlined hide-details>
            </v-text-field>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12">
            <v-combobox
              v-model="form.recipients"
              :search-input.sync="recipientInput"
              label="Recipients"
              multiple
              small-chips
              deletable-chips
              dense outlined hide-details
              append-icon="">
            </v-combobox>
          </v-col>
        </v-row>
        <v-row v-if="error" dense>
          <v-col cols="12" class="pt-0">
            <span class="error--text caption">{{ error }}</span>
          </v-col>
        </v-row>
      </div>
    </v-container>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn v-if="editingId" text class="mr-4" @click="resetForm">Cancel edit</v-btn>
      <v-btn
        color="primary"
        :loading="saving"
        :disabled="!canSave"
        @click="saveSchedule">
        {{ editingId ? 'Update' : 'Create' }}
      </v-btn>
    </v-card-actions>

    <v-dialog v-model="showDeleteConfirm" max-width="420">
      <v-card>
        <v-card-title class="subtitle-1">Delete scheduled delivery?</v-card-title>
        <v-card-text>
          This will stop the recurring delivery of
          <strong>{{ pendingDelete ? templateName(pendingDelete.template_id) : '' }}</strong>.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showDeleteConfirm = false">Cancel</v-btn>
          <v-btn color="error" text @click="confirmDelete">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import Items from '@/api/endpoints'
import { displayAlert } from '@/mixins/alert'

// Email format only (any domain); anchored + whitespace-free to reject malformed input.
const EMAIL_REGEX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

export default {
  name: 'ScheduledExportsPanel',
  mixins: [displayAlert],
  data () {
    return {
      templates: [],
      schedules: [],
      loading: false,
      saving: false,
      error: '',
      editingId: null,
      recipientInput: '',
      showDeleteConfirm: false,
      pendingDelete: null,
      form: this.emptyForm(),
      headers: [
        { text: 'Template', value: 'template_id', sortable: false },
        { text: 'Frequency', value: 'frequency', sortable: false },
        { text: 'Recipients', value: 'recipients', sortable: false },
        { text: 'Active', value: 'active', sortable: false, align: 'center' },
        { text: '', value: 'actions', sortable: false, align: 'end' }
      ],
      frequencyOptions: [
        { text: 'Daily', value: 'daily' },
        { text: 'Weekly', value: 'weekly' },
        { text: 'Monthly', value: 'monthly' }
      ],
      weekdayOptions: [
        { text: 'Monday', value: 0 },
        { text: 'Tuesday', value: 1 },
        { text: 'Wednesday', value: 2 },
        { text: 'Thursday', value: 3 },
        { text: 'Friday', value: 4 },
        { text: 'Saturday', value: 5 },
        { text: 'Sunday', value: 6 }
      ]
    }
  },
  async mounted () {
    await Promise.all([this.fetchTemplates(), this.fetchSchedules()])
  },
  computed: {
    canSave () {
      const hasRecipients = this.form.recipients.length > 0 || Boolean((this.recipientInput || '').trim())
      if (!this.form.template_id || !this.form.frequency || !hasRecipients) {
        return false
      }
      if (this.form.frequency === 'weekly' && this.form.day_of_week === null) {
        return false
      }
      if (this.form.frequency === 'monthly' &&
        !(Number.isInteger(this.form.day_of_month) && this.form.day_of_month >= 1 && this.form.day_of_month <= 31)) {
        return false
      }
      return true
    }
  },
  methods: {
    emptyForm () {
      return {
        template_id: null,
        frequency: 'weekly',
        day_of_week: null,
        day_of_month: null,
        recipients: [],
        active: true
      }
    },
    templateName (templateId) {
      const match = this.templates.find(t => t.template_id === templateId)
      return match ? match.name : '(deleted template)'
    },
    frequencyLabel (item) {
      if (item.frequency === 'weekly') {
        const day = this.weekdayOptions.find(d => d.value === item.day_of_week)
        return `Weekly (${day ? day.text : '-'})`
      }
      if (item.frequency === 'monthly') {
        return `Monthly (day ${item.day_of_month})`
      }
      return 'Daily'
    },
    async fetchTemplates () {
      try {
        const res = await Items.getExportTemplates()
        this.templates = (res && res.items) ? res.items : []
      } catch (err) {
        this.error = 'Unable to load templates.'
      }
    },
    async fetchSchedules () {
      this.loading = true
      try {
        const res = await Items.getExportSchedules()
        this.schedules = (res && res.items) ? res.items : []
      } catch (err) {
        this.error = 'Unable to load scheduled deliveries.'
      } finally {
        this.loading = false
      }
    },
    editSchedule (item) {
      this.error = ''
      this.editingId = item.schedule_id
      this.form = {
        template_id: item.template_id,
        frequency: item.frequency,
        day_of_week: item.day_of_week,
        day_of_month: item.day_of_month,
        recipients: [...(item.recipients || [])],
        active: item.active
      }
    },
    resetForm () {
      this.editingId = null
      this.form = this.emptyForm()
      this.recipientInput = ''
      this.error = ''
    },
    commitRecipientInput () {
      // v-combobox only adds a chip on enter/blur; fold any residual typed text in.
      const text = (this.recipientInput || '').trim()
      if (text && !this.form.recipients.includes(text)) {
        this.form.recipients.push(text)
      }
      this.recipientInput = ''
    },
    validateRecipients () {
      const invalid = this.form.recipients.filter(r => !EMAIL_REGEX.test(String(r).trim()))
      if (invalid.length > 0) {
        this.error = `Recipients must be valid email addresses: ${invalid.join(', ')}`
        return false
      }
      return true
    },
    buildPayload () {
      return {
        template_id: this.form.template_id,
        frequency: this.form.frequency,
        day_of_week: this.form.frequency === 'weekly' ? this.form.day_of_week : null,
        day_of_month: this.form.frequency === 'monthly' ? this.form.day_of_month : null,
        recipients: this.form.recipients.map(r => String(r).trim()),
        active: this.form.active
      }
    },
    async saveSchedule () {
      this.error = ''
      this.commitRecipientInput()
      if (!this.validateRecipients()) {
        return
      }
      this.saving = true
      const editing = !!this.editingId
      try {
        if (editing) {
          await Items.updateExportSchedule(this.editingId, this.buildPayload())
        } else {
          await Items.createExportSchedule(this.buildPayload())
        }
        this.resetForm()
        await this.fetchSchedules()
        this.emitAlert(true, 'success', editing ? 'Schedule updated' : 'Schedule created', undefined, 3000)
      } catch (err) {
        const status = err && err.response ? err.response.status : null
        this.error = status === 422
          ? 'Please check the schedule fields and recipient addresses.'
          : 'Unable to save the schedule.'
      } finally {
        this.saving = false
      }
    },
    promptDelete (item) {
      this.pendingDelete = item
      this.showDeleteConfirm = true
    },
    confirmDelete () {
      const item = this.pendingDelete
      this.showDeleteConfirm = false
      this.pendingDelete = null
      if (item) {
        this.deleteSchedule(item)
      }
    },
    async deleteSchedule (item) {
      this.error = ''
      try {
        await Items.deleteExportSchedule(item.schedule_id)
        if (this.editingId === item.schedule_id) {
          this.resetForm()
        }
        await this.fetchSchedules()
        this.emitAlert(true, 'success', 'Schedule deleted', undefined, 3000)
      } catch (err) {
        this.error = 'Unable to delete the schedule.'
      }
    },
    async toggleActive (item) {
      try {
        await Items.updateExportSchedule(item.schedule_id, {
          template_id: item.template_id,
          frequency: item.frequency,
          day_of_week: item.frequency === 'weekly' ? item.day_of_week : null,
          day_of_month: item.frequency === 'monthly' ? item.day_of_month : null,
          recipients: item.recipients,
          active: !item.active
        })
        this.emitAlert(true, 'success', item.active ? 'Schedule paused' : 'Schedule activated', undefined, 3000)
        await this.fetchSchedules()
      } catch (err) {
        this.error = 'Unable to update the schedule.'
        await this.fetchSchedules()
      }
    },
    rowClass (item) {
      return item.schedule_id === this.editingId ? 'editing-row' : ''
    }
  }
}
</script>
<style scoped>
.schedules-table >>> td,
.schedules-table >>> th {
  font-size: 0.95rem;
}
.schedules-table >>> tr.editing-row td {
  background-color: #e3f2fd;
}
.schedule-form {
  max-width: 620px;
}
.recipients-cell {
  max-width: 260px;
}
</style>



// WEBPACK FOOTER //
// src/components/ScheduledExportsPanel.vue